import * as React from 'react';
import { range } from 'lodash';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
    useGetOwnedAlerts,
    useGetSubscribedAlerts,
} from 'lib/alert/hooks/useGetAlerts';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Flex from 'ui/Flex/Flex';
import { sortAlerts } from 'lib/alert/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import AlertPreview from './AlertPreview';
import AlertLoading from './Alert.loading';
import { useAlertAction } from './Contexts/AlertActionContext';

type Props = {
    chartKey: number;
};

const StyDrawerContentText = styled(Typography, {
    name: 'StyDrawerContentText',
})({
    fontWeight: 300,
    color: '#666',
    lineHeight: '1.5em',
    padding: '1rem 0',
});

const AlertList = ({ chartKey }: Props): JSX.Element => {
    const { t } = useTranslation();
    const { setAction } = useAlertAction();

    const goToAlertStatus = (alertId: number) => () => {
        setAction({ name: 'status', alertId });
        trackEvent('decision/alerts/viewStatus');
    };

    const {
        data: ownedAlerts,
        isLoading: areOwnedAlertsLoading,
        isFetching: areOwnedAlertsFetching,
    } = useGetOwnedAlerts('chart', chartKey);
    const {
        data: subscribedAlerts,
        isLoading: areSubscribedAlertsLoading,
        isFetching: areSubscribedAlertsFetching,
    } = useGetSubscribedAlerts('chart', chartKey);

    const sortedOwnedAlerts = ownedAlerts ? sortAlerts(ownedAlerts) : [];
    const sortedSubscribedAlerts = subscribedAlerts
        ? sortAlerts(subscribedAlerts)
        : [];

    return (
        <>
            <StyDrawerContentText>
                {t('alert.list.description')}
            </StyDrawerContentText>

            <Flex align="center" justify="space-between">
                <Typography variant="subtitle1">
                    {t('alert.list.myAlerts')}
                </Typography>
                <Button
                    onClick={() => setAction({ name: 'create' })}
                    startIcon={<AddIcon />}
                >
                    {t('alert.create.title')}
                </Button>
            </Flex>

            {areOwnedAlertsLoading || areOwnedAlertsFetching
                ? range(2).map((number) => <AlertLoading key={number} />)
                : sortedOwnedAlerts.map((alert) => (
                      <AlertPreview
                          key={alert.id}
                          alert={alert}
                          chartKey={chartKey}
                          onClick={goToAlertStatus(alert.id)}
                      />
                  ))}

            {/* Empty State */}
            {ownedAlerts?.length === 0 && (
                <Box sx={{ mx: 'auto', p: '2rem' }}>
                    {t('alert.list.empty')}
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ mt: 4 }}>
                {t('alert.list.mySubscribedAlerts')}
            </Typography>

            {areSubscribedAlertsLoading || areSubscribedAlertsFetching
                ? range(2).map((number) => <AlertLoading key={number} />)
                : sortedSubscribedAlerts?.map((alert) => (
                      <AlertPreview
                          key={alert.id}
                          alert={alert}
                          chartKey={chartKey}
                          isSubscribedAlert
                          onClick={goToAlertStatus(alert.id)}
                      />
                  ))}

            {/* Empty State */}
            {subscribedAlerts?.length === 0 && (
                <Box sx={{ mx: 'auto', p: '2rem' }}>
                    {t('alert.list.emptySubscribed')}
                </Box>
            )}
        </>
    );
};

export default AlertList;
